import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { useRef, useState, type KeyboardEvent } from "react";
import { getErrorMessage } from "../../api/client.ts";
import {
  DEFAULT_SNOOZE_DAYS,
  PRESETS,
  dateInputInDays,
  morningOf,
  tomorrowInput,
} from "./snooze.ts";
import type { ReviewCopy, ReviewDecision, UseReview } from "./types.ts";

const COPY = {
  accept: {
    verb: "Pursue",
    color: "success",
    placeholder: "What makes this worth pursuing?",
  },
  disqualify: {
    verb: "Disqualify",
    color: "error",
    placeholder: "Why is this not a fit?",
  },
} as const;

/**
 * Asks for feedback before a Pursue or Disqualify goes through. The feedback is
 * optional; it reaches the agents that found the thing as the reviewer's note.
 * Enter sends (not from a button), Shift+Enter starts a new line. Pursue &
 * snooze also picks the day the rows come back on the worklist.
 *
 * With `onSubmitted` the dialog closes as soon as the decision is sent and
 * hands it over (the drawer moves on to the next row); a failure toasts and
 * refreshes the list. Without it, it waits and shows the error.
 */
export default function ReviewDecisionDialog({
  decision,
  onClose,
  onDone,
  onSubmitted,
  useReview,
  copy,
}: {
  decision: ReviewDecision | null;
  onClose: () => void;
  onDone?: () => void;
  onSubmitted?: (decision: ReviewDecision) => void;
  useReview: UseReview;
  copy: ReviewCopy;
}) {
  const { review } = useReview(onDone);
  const [feedback, setFeedback] = useState("");
  const input = useRef<HTMLTextAreaElement>(null);
  const [snoozeDate, setSnoozeDate] = useState("");
  // Every Pursue & snooze starts a week out.
  const [openedFor, setOpenedFor] = useState(decision);
  if (decision !== openedFor) {
    setOpenedFor(decision);
    if (decision?.snooze) setSnoozeDate(dateInputInDays(DEFAULT_SNOOZE_DAYS));
  }
  // Typed dates can skip the picker's `min`.
  const dateOk = !decision?.snooze || (!!snoozeDate && snoozeDate >= tomorrowInput());
  const action = COPY[decision?.action ?? "accept"];
  const verb = decision?.snooze ? "Pursue & snooze" : action.verb;
  const count = decision?.ids.length ?? 0;

  const close = () => {
    setFeedback("");
    review.reset();
    onClose();
  };
  const submit = () => {
    if (!decision || !dateOk) return;
    const vars = {
      ids: decision.ids,
      action: decision.action,
      reason: feedback.trim() || null,
      snoozeUntil: decision.snooze ? morningOf(snoozeDate) : null,
    };
    if (onSubmitted) {
      review.mutate(vars);
      close();
      onSubmitted(decision);
      return;
    }
    if (!review.isPending) review.mutate(vars, { onSuccess: close });
  };
  // Enter sends from anywhere but the buttons and chips (they keep their own Enter).
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== "Enter" || e.shiftKey || e.nativeEvent.isComposing) return;
    if (e.target instanceof Element && e.target.closest('button, [role="button"]')) return;
    e.preventDefault();
    submit();
  };

  return (
    <Dialog
      open={!!decision}
      onClose={close}
      onKeyDown={onKeyDown}
      maxWidth="xs"
      fullWidth
      // The focus trap takes focus to the dialog itself; the field wants it.
      slotProps={{ transition: { onEntered: () => input.current?.focus() } }}
    >
      <DialogTitle>
        {verb} {count === 1 ? copy.noun : `${count} ${copy.noun}s`}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {decision?.snooze && (
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 1 }}
            >
              <TextField
                type="date"
                size="small"
                label="Back on the worklist"
                value={snoozeDate}
                onChange={(e) => setSnoozeDate(e.target.value)}
                error={!dateOk}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: tomorrowInput() } }}
              />
              {PRESETS.map((p) => {
                const value = dateInputInDays(p.days);
                const on = snoozeDate === value;
                return (
                  <Chip
                    key={p.days}
                    size="small"
                    label={p.label}
                    color={on ? "primary" : "default"}
                    variant={on ? "filled" : "outlined"}
                    onClick={() => setSnoozeDate(value)}
                  />
                );
              })}
            </Stack>
          )}
          <TextField
            label="Feedback (optional)"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            inputRef={input}
            placeholder={action.placeholder}
            helperText={`${copy.feedbackHelp} · Enter sends`}
            autoFocus
            fullWidth
            multiline
            minRows={3}
            size="small"
          />
          {review.isError && <Alert severity="error">{getErrorMessage(review.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button
          variant="contained"
          color={action.color}
          disabled={!dateOk || (!onSubmitted && review.isPending)}
          onClick={submit}
        >
          {verb}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
